// Общий layout-компонент для форм документов (split-layout)
// Используется в: DocumentViewDialog, DocumentDialog

import React from 'react';
import {
    Dialog,
    Box,
    Paper,
    Typography,
    IconButton,
    Divider,
    CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';

// Цветовая схема - экспортируется для использования в дочерних компонентах
export const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
    dark: '#1e293b',
    light: '#f8fafc',
    border: '#e2e8f0',
    white: '#ffffff'
};

interface DocumentLayoutProps {
    open: boolean;
    onClose: () => void;
    /** Основной заголовок, напр. "ДОКУМЕНТ" или "НОВЫЙ ДОКУМЕНТ" */
    title: string;
    /** Подзаголовок, напр. "№123" */
    subtitle?: string;
    /** Бейджи в header (статус, тип, прогресс) */
    headerBadges?: React.ReactNode;
    /** Дополнительные кнопки в header (напр. "Создать на основании") */
    headerActions?: React.ReactNode;
    /** Содержимое левой панели (sidebar) */
    sidebar: React.ReactNode;
    /** Основной контент (правая панель) */
    children: React.ReactNode;
    /** Футер с кнопками действий */
    footer?: React.ReactNode;
    /** Показать лоадер */
    loading?: boolean;
    /** Блокировать ESC */
    disableEscapeKeyDown?: boolean;
}

const DocumentLayout: React.FC<DocumentLayoutProps> = ({
    open,
    onClose,
    title,
    subtitle,
    headerBadges,
    headerActions,
    sidebar,
    children,
    footer,
    loading = false,
    disableEscapeKeyDown = false
}) => {
    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason === 'backdropClick') {
                    return;
                }
                onClose();
            }}
            maxWidth="xl"
            fullWidth
            disableEscapeKeyDown={disableEscapeKeyDown}
            PaperProps={{
                sx: {
                    height: '90vh',
                    bgcolor: '#f8fafc',
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Header */}
                    <Paper elevation={0} sx={{
                        px: 3, py: 2,
                        borderBottom: `1px solid ${colors.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: 'white',
                        zIndex: 1,
                        flexShrink: 0
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>{title}</Typography>
                                {subtitle && (
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: colors.dark, lineHeight: 1 }}>
                                        {subtitle}
                                    </Typography>
                                )}
                            </Box>

                            {headerBadges && (
                                <>
                                    <Divider orientation="vertical" flexItem sx={{ height: 32, alignSelf: 'center', mx: 1 }} />
                                    {headerBadges}
                                </>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {headerActions}
                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: 2,
                                    transition: '0.2s',
                                    '&:hover': { bgcolor: colors.error, color: 'white', borderColor: colors.error }
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Box>
                    </Paper>

                    {/* Body: Sidebar + Main Content */}
                    <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
                        {/* Sidebar / Left Panel */}
                        <Box sx={{
                            width: 380,
                            borderRight: `1px solid ${colors.border}`,
                            bgcolor: 'white',
                            overflow: 'auto',
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {sidebar}
                        </Box>

                        {/* Main Content / Right Panel */}
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc', overflow: 'hidden' }}>
                            {children}
                        </Box>
                    </Box>

                    {/* Footer (optional) */}
                    {footer && (
                        <Paper elevation={0} sx={{
                            px: 3, py: 2,
                            borderTop: `1px solid ${colors.border}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: 'white',
                            flexShrink: 0
                        }}>
                            {footer}
                        </Paper>
                    )}
                </>
            )}
        </Dialog>
    );
};

export default DocumentLayout;
